import Tabs from '../tabs';
import i18n, { t } from '../i18n';
import userData from '../user-data';
import type { UserData } from '../user-data';
import toggle from '@nt-workspace/common/build/helpers/toggle';

import License from './license';
import Model from './model';
import ModelNA from './model-na';
import Size from './size';

declare global {
  interface Window {
    ntTireSelectorData: {
      [elementId: string]: {
        settings: TireSelectorSettingsFromServer;
        translations: Record<string, string>;
      };
    };
  }
}

export type SpeedRating = {
  /** Speed rating code */
  code: string;
  /** Speed in either kmh or mph */
  speed: number;
  /** Unit of speed */
  unit: string;
}

export type TireSize = {
  /* Short size string, e.g. 215/60R16 */
  size: string;
  /* Tire width in mm */
  width: number;
  /* Tire height is actually aspect ratio (height to width ratio) */
  height: number;
  /* Rim diameter in inches */
  rim: number;
  /* Speed index code, a letter */
  speedIndex: string;
  /* Load index code, a number */
  loadIndex: number;
};

type TireSelectorSettingsFromServer = Omit<TireSelectorSettings, 'sizeData'> & {
  /* Available NT tire sizes as arrays */
  sizeData: [
    number, // tire width (mm)
    number, // tire height, (aspect ratio) (%)
    number, // rim diameter (inches)
    number, // load index (number)
    string, // speed index (letter)
  ][];
};

type TireSelectorSettings = {
  /** Stage language code */
  lang: string;
  /* Tire selector type */
  type: 'standalone' | 'embed';
  /* Tire selector version controls which api and modules it uses */
  version: 'fitment' | 'fitmentna';
  /* Uses old or new tires pages */
  tiresVersion: 'old' | 'new';
  /* URL template for tire size page, _size_slug_ will be replaced with actual size */
  byTireSizeUrl: string;
  /* Available NT tire sizes */
  sizeData: TireSize[];
  /* Speed rating table */
  speedRatings: SpeedRating[];
  /** Control whether model/license module autoredirects to factory size on submit */
  autoRedirect: boolean;
  /** Control whether the tire selector allows collapsing to a smaller view */
  enableCollapsing: boolean;
};

export class TireSelector {
  // Store last active tab to session storage
  #storageTabKey = 'ts__activeView';
  // Internal name for logging
  name = 'TS';

  dom: {
    root: HTMLElement;
    tabPanels: HTMLElement[];
    tabList: HTMLElement;
    collapseToggle?: HTMLElement;
    collapsedContentContainer?: HTMLElement;
  };

  tablist!: Tabs;

  modules!: {
    size: Size,
    license: License,
    model: Model | ModelNA,
  };

  settings!: TireSelectorSettings;
  sizesTemplate!: string;

  constructor(element:HTMLElement, name?:string) {
    if(name) {
      this.name = name;
    }

    this.dom = {
      root: element,
      tabPanels: Array.from(element.querySelectorAll('[role="tabpanel"]')),
      tabList: element.querySelector('[role="tablist"]') as HTMLElement,
      collapseToggle: element.querySelector('[data-ts-toggle]') as HTMLElement,
      collapsedContentContainer: element.querySelector('[data-ts-collapse-content]') as HTMLElement,
    };

    // Initialise tabbed interface
    this.#initTabs();

    // Init templates
    this.sizesTemplate = element.querySelector('[data-sizes-template]')?.innerHTML || '';

    if(!this.sizesTemplate) {
      console.error(`[${this.name}]: missing sizes template`, this);
      return;
    }

    // Init settings
    const elementId = this.dom.root.id;

    if(window.ntTireSelectorData[elementId]) {
      const settings = {
        ...window.ntTireSelectorData[elementId].settings,
        sizeData: this.#formatTireSizes(window.ntTireSelectorData[elementId].settings.sizeData),
      }

      this.settings = settings;

      // Init translations
      if (window.window.ntTireSelectorData[elementId].translations) {
        // Add language name space to translations, prefix all keys with this.settings.language + '.'
        const translations = {};
        Object.keys(window.window.ntTireSelectorData[elementId].translations).forEach(key => {
          translations[`${this.settings.lang}.${key}`] = window.window.ntTireSelectorData[elementId].translations[key];
        });
        i18n.addTranslations(translations);
      }
    } else {
      console.error(`[${this.name}]: missing settings for element id:`, elementId);
      return;
    }

    // Init modules per tab
    this.modules = {} as any;
    this.dom.tabPanels.forEach(tabEl => {
      if (tabEl.dataset.type === 'size') {
        const moduleRootElement = tabEl.firstElementChild as HTMLElement;
        if(moduleRootElement) {
          this.modules.size = this.createSizeModule(moduleRootElement);
        }
      }

      if (tabEl.dataset.type === 'license') {
        const moduleRootElement = tabEl.firstElementChild as HTMLElement;
        if(moduleRootElement) {
          this.modules.license = this.createLicenseModule(moduleRootElement);
        }
      }

      if (tabEl.dataset.type === 'model') {
        const moduleRootElement = tabEl.firstElementChild as HTMLElement;
        if(moduleRootElement) {
          if(this.settings.version === 'fitment') {
            this.modules.model = this.createModelModule(moduleRootElement);
          } else if(this.settings.version === 'fitmentna') {
            this.modules.model = this.createNAModelModule(moduleRootElement);
          }
        }
      }
    });

    // Init collapsible behavior
    if(this.settings.enableCollapsing) {
      this.#initCollapseBehavior();
    }

    // Loading state was set before redirect, on back button navigation it is still there because of bfcache.
    // Remove loading state if page from cache.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        this.setLoading(false);
      }
    });

    console.log(`[${this.name}] init()`);
    this.log('init settings', this);
  }

  /**
   * Initialise tabbed interface functionality and
   * sets last viewed tab from session storage as active
   */
  #initTabs() {
    this.tablist = new Tabs(this.dom.tabList);

    // Set active tab from session storage
    const activeTabType = sessionStorage.getItem(this.#storageTabKey);
    const activeTabId = this.dom.root.querySelector(`[role="tab"][data-type="${activeTabType}"]`)?.id;
    if (activeTabId) {
      this.tablist.showTab(activeTabId, false);
    }

    // Store active tab to session storage on change
    // Note: all tire selectors on page get same active tab on page load from here
    this.dom.tabList.addEventListener('tabs.change', (e: Event) => {
      const activeTabType = (this.dom.root.querySelector(`[role="tab"][aria-selected="true"]`) as HTMLElement)?.dataset?.type;
      if (activeTabType) {
        sessionStorage.setItem(this.#storageTabKey, activeTabType);
      }
    });
  }

  #initCollapseBehavior() {
    if(!this.dom.collapseToggle || !this.dom.collapsedContentContainer) {
      console.error(`[${this.name}]: missing collapse toggle or content container elements for collapsing behavior`, this.dom);
      return;
    }

    // Subscribe to userdata changes
    userData.subscribe('any', this.updateCollapsedContent.bind(this));

    // Set initial state on load
    this.updateCollapsedContent(userData.get('any'));

    // Set collapsed state on init if there is content to show in collapsed view
    if(!!this.dom.collapsedContentContainer.innerHTML) {
      toggle.toggle(this.dom.collapseToggle, false);
    }

    console.log('init TS collapse behavior');
  }

  /**
   * Listens to user data changes and updates the collapsed view content accordingly.
   */
  updateCollapsedContent(data: UserData) {
    console.log(`[${this.name}] updateCollapsedContent()`, data);
    if(!this.dom.collapseToggle || !this.dom.collapsedContentContainer) {
      return;
    }

    let markup = '';

    // We're only interested in userdata that corresponds with TS modules
    if(this.modules.size) {
      if(data.selectedSize) {
        markup = `${data.selectedSize.size}`;
      }
    }

    if(this.modules.model) {
      if(this.settings.version === 'fitment' && data.vehicle) {
        markup = `${data.vehicle.manufacturerName} ${data.vehicle.modelName}`;
        if(data.selectedSize) {
          markup += `<br>${data.selectedSize.size}`;
        }
      } else if(this.settings.version === 'fitmentna' && data.vehicleNA) {
        markup = `${data.vehicleNA.manufacturer} ${data.vehicleNA.model}`;
        if(data.selectedSize) {
          markup += `<br>${data.selectedSize.size}`;
        }
      }
    }

    if(this.modules.license) {
      if(data?.license?.vehicleData?.modelData) {
        markup = `${data.license.vehicleData.modelData.manufacturerName} ${data.license.vehicleData.modelData.modelName}`;
        if(data.selectedSize) {
          markup += `<br>${data.selectedSize.size}`;
        }
      }
    }

    if(markup) {
      this.dom.collapsedContentContainer.innerHTML = markup;
      this.dom.collapseToggle.hidden = false;
    } else {
      this.dom.collapsedContentContainer.innerHTML = '';
      this.dom.collapseToggle.hidden = true;
      toggle.toggle(this.dom.collapseToggle, true);
    }
  }

  /**
   * Formats tire sizes from backend to TireSize objects
   */
  #formatTireSizes(sizes: TireSelectorSettingsFromServer['sizeData']): TireSize[] {
    return sizes.map(item => {
      return {
        size: `${item[0]}/${item[1]}R${item[2]}`,
        width: item[0],
        height: item[1],
        rim: item[2],
        loadIndex: item[3],
        speedIndex: item[4],
      };
    });
  }

  setLoading(isLoading: boolean) {
    this.dom.root.classList.toggle('loading', isLoading);
  }

  /**
   * Handles tire size selection from child modules
   *
   * @param sizeStr - '205/55R16'
   */
  onSizeSelect(sizeStr: string) {
    this.log('onSizeSelect()', sizeStr);

    // Split size string into parts
    const sizeObj = this.settings.sizeData.find(size => size.size === sizeStr);

    if(!sizeObj) {
      console.error(`[${this.name}].onSizeSelect(): selected size not found in available sizes`, sizeStr);
      return;
    }

    // Store selected size to user data
    userData.store({
      selectedSize: {
        width: sizeObj.width,
        height: sizeObj.height,
        rim: sizeObj.rim,
        size: sizeObj.size,
      },
    });

    this.sendAnalyticsEvent({
      event: 'tyre_search_submit',
      tyre_search_type: 'Size select',
      tyre_size: sizeObj.size,
    });

    // Redirect to size page
    this.doActionOnSizeSelect(sizeObj);
  }

  /**
   * Handles action to be done on size select.
   * This is the final step of tire selector flow.
   * Override in subclasses, like product tire selector.
   */
  doActionOnSizeSelect(sizeObj: TireSize) {
    this.log('doActionOnSizeSelect()', sizeObj);

    this.redirectToSizePage(sizeObj);
  }

  redirectToSizePage(sizeObj: TireSize) {
    this.setLoading(true);
    const sizeSlug = `${sizeObj.width}-${sizeObj.height}-${sizeObj.rim}`;
    const sizeUrl = this.settings.byTireSizeUrl.replace('_size_slug_', sizeSlug);
    window.location.href = sizeUrl;
  }

  sendAnalyticsEvent(analyticsEvent) {
    // search type and size need to be sent always
    if (analyticsEvent) {
      analyticsEvent.event = 'tyre_search_submit';
      analyticsEvent.tyreselector_name = this.name;

      this.log(`sendAnalyticsEvent`, analyticsEvent);

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(analyticsEvent);
    }
  }

  log(message: string, ...optionalParams: any[]) {
    // console.log(`[${this.name}] ${message}`, ...optionalParams);
  }

  /**
   * Factory method for creating Size module.
   * Override in subclasses to use custom Size implementations.
   */
  protected createSizeModule(element: HTMLElement): Size {
    return new Size(element, this);
  }

  /**
   * Factory method for creating License module.
   * Override in subclasses to use custom License implementations.
   */
  protected createLicenseModule(element: HTMLElement): License {
    return new License(element, this);
  }

  /**
   * Factory method for creating Model module.
   * Override in subclasses to use custom Model implementations.
   */
  protected createModelModule(element: HTMLElement): Model {
    return new Model(element, this);
  }

  /**
   * Factory method for creating Model module.
   * Override in subclasses to use custom ModelNA implementations.
   */
  protected createNAModelModule(element: HTMLElement): ModelNA {
    return new ModelNA(element, this);
  }
}

export function init(elements: NodeListOf<HTMLElement>) {
  for (let element of elements) {
    new TireSelector(element);
  }
}
